import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Summary {
  today: { cost: number; tokens: number; calls: number };
  thisMonth: { cost: number; tokens: number; calls: number };
  allTime: { cost: number; tokens: number; calls: number };
}

interface ModuleCost {
  module: string;
  _sum: { costUSD: number; totalTokens: number };
  _count: number;
}

interface LogEntry {
  id: string;
  userId: string | null;
  userEmail: string | null;
  module: string;
  feature: string;
  functionName: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUSD: number;
  durationMs: number;
  success: boolean;
  errorMessage: string | null;
  createdAt: string;
}

interface TopUser {
  userId: string;
  userEmail: string;
  _sum: { costUSD: number; totalTokens: number };
  _count: number;
}

const moduleLabels: Record<string, string> = {
  chat: '💬 Chat AI',
  nutrition: '🥗 Nutriție',
  exercises: '🏋️ Exerciții',
  therapy: '🧠 Terapie',
  food_tracking: '📸 Food Tracking',
  dashboard: '📊 Dashboard',
};

const moduleColors: Record<string, string> = {
  chat: 'bg-blue-100 text-blue-700',
  nutrition: 'bg-green-100 text-green-700',
  exercises: 'bg-purple-100 text-purple-700',
  therapy: 'bg-pink-100 text-pink-700',
  food_tracking: 'bg-orange-100 text-orange-700',
  dashboard: 'bg-indigo-100 text-indigo-700',
};

export default function AdminAIUsage() {
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [byModule, setByModule] = useState<ModuleCost[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'logs' | 'users'>('overview');
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [filterModule, setFilterModule] = useState('');

  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [logPage, filterModule, activeTab]);

  const headers = { Authorization: `Bearer ${accessToken}` };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [summaryRes, moduleRes, topUsersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/ai-usage/summary`, { headers }),
        axios.get(`${API_URL}/api/admin/ai-usage/by-module`, { headers }),
        axios.get(`${API_URL}/api/admin/ai-usage/top-users?limit=10`, { headers }),
      ]);
      setSummary(summaryRes.data);
      setByModule(moduleRes.data || []);
      setTopUsers(topUsersRes.data || []);
    } catch (err: any) {
      console.error('Error fetching AI usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params: any = { page: logPage, limit: 30 };
      if (filterModule) params.module = filterModule;
      const res = await axios.get(`${API_URL}/api/admin/ai-usage/logs`, { headers, params });
      setLogs(res.data.logs || []);
      setLogTotal(res.data.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching logs:', err);
    }
  };

  const formatCost = (cost: number) => {
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  const formatDuration = (ms: number) => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}min`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă datele AI...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
              ADMIN
            </div>
            <span className="text-gray-600">🤖 AI Usage & Costs</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← Înapoi la Admin
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🤖 AI Usage & Costs Monitor
          </h1>
          <p className="text-gray-600">Monitorizează costurile FII FIT AI pe fiecare modul și funcție</p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Today */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg p-6">
              <div className="text-blue-100 text-sm font-medium mb-1">📅 Astăzi</div>
              <div className="text-3xl font-bold mb-3">{formatCost(summary.today.cost)}</div>
              <div className="flex justify-between text-blue-100 text-sm">
                <span>{summary.today.calls} apeluri</span>
                <span>{formatTokens(summary.today.tokens)} tokens</span>
              </div>
            </div>

            {/* This Month */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl shadow-lg p-6">
              <div className="text-green-100 text-sm font-medium mb-1">📆 Luna aceasta</div>
              <div className="text-3xl font-bold mb-3">{formatCost(summary.thisMonth.cost)}</div>
              <div className="flex justify-between text-green-100 text-sm">
                <span>{summary.thisMonth.calls} apeluri</span>
                <span>{formatTokens(summary.thisMonth.tokens)} tokens</span>
              </div>
            </div>

            {/* All Time */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl shadow-lg p-6">
              <div className="text-purple-100 text-sm font-medium mb-1">🏆 Total</div>
              <div className="text-3xl font-bold mb-3">{formatCost(summary.allTime.cost)}</div>
              <div className="flex justify-between text-purple-100 text-sm">
                <span>{summary.allTime.calls} apeluri</span>
                <span>{formatTokens(summary.allTime.tokens)} tokens</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
          <div className="flex border-b border-gray-200">
            {[
              { key: 'overview', label: '📊 Overview per Modul' },
              { key: 'logs', label: '📋 Loguri Detaliate' },
              { key: 'users', label: '👥 Top Utilizatori' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex-1 px-6 py-4 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab - Cost by Module */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Module Breakdown */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Cost per Modul</h2>
              </div>
              {byModule.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  Nu există date încă. Folosește platforma pentru a genera loguri AI.
                </div>
              ) : (
                <div className="p-6">
                  <div className="space-y-4">
                    {byModule.map((item: any) => {
                      const cost = item.totalCost || item._sum?.costUSD || 0;
                      const tokens = item.totalTokens || item._sum?.totalTokens || 0;
                      const calls = item.callCount || item._count || 0;
                      const maxCost = Math.max(...byModule.map((m: any) => m.totalCost || m._sum?.costUSD || 0));
                      const percentage = maxCost > 0 ? (cost / maxCost) * 100 : 0;
                      return (
                        <div key={item.module} className="flex items-center gap-4">
                          <div className="w-40">
                            <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${moduleColors[item.module] || 'bg-gray-100 text-gray-700'}`}>
                              {moduleLabels[item.module] || item.module}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full flex items-center justify-end pr-2 transition-all"
                                style={{ width: `${Math.max(percentage, 5)}%` }}
                              >
                                <span className="text-white text-xs font-bold">
                                  {formatCost(cost)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="w-32 text-right text-sm text-gray-600">
                            {calls} apeluri
                          </div>
                          <div className="w-28 text-right text-sm text-gray-600">
                            {formatTokens(tokens)} tok
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Total */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <div className="flex gap-8">
                      <span className="text-lg font-bold text-green-600">
                        {formatCost(byModule.reduce((sum: number, m: any) => sum + (m.totalCost || m._sum?.costUSD || 0), 0))}
                      </span>
                      <span className="text-sm text-gray-600">
                        {byModule.reduce((sum: number, m: any) => sum + (m.callCount || m._count || 0), 0)} apeluri
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Loguri AI ({logTotal})
              </h2>
              <select
                value={filterModule}
                onChange={(e) => { setFilterModule(e.target.value); setLogPage(1); }}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Toate modulele</option>
                <option value="chat">💬 Chat</option>
                <option value="nutrition">🥗 Nutriție</option>
                <option value="exercises">🏋️ Exerciții</option>
                <option value="therapy">🧠 Terapie</option>
                <option value="food_tracking">📸 Food Tracking</option>
                <option value="dashboard">📊 Dashboard</option>
              </select>
            </div>

            {logs.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                Nu există loguri.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modul</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feature</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tokens</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durată</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {logs.map((log) => (
                        <tr key={log.id} className={`hover:bg-gray-50 ${!log.success ? 'bg-red-50' : ''}`}>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                            {new Date(log.createdAt).toLocaleString('ro-RO', {
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${moduleColors[log.module] || 'bg-gray-100 text-gray-700'}`}>
                              {log.module}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-900 max-w-[150px] truncate" title={log.feature}>
                            {log.feature}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 font-mono">
                            {log.model}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            <span title={`Prompt: ${log.promptTokens} | Completion: ${log.completionTokens}`}>
                              {formatTokens(log.totalTokens)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs font-bold text-green-700">
                            {formatCost(log.costUSD)}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {formatDuration(log.durationMs)}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 max-w-[120px] truncate" title={log.userEmail || log.userId || 'N/A'}>
                            {log.userEmail || log.userId?.slice(0, 8) || 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            {log.success ? (
                              <span className="text-green-600 text-xs">✅</span>
                            ) : (
                              <span className="text-red-600 text-xs" title={log.errorMessage || ''}>❌</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Pagina {logPage} din {Math.ceil(logTotal / 30)} ({logTotal} total)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLogPage(Math.max(1, logPage - 1))}
                      disabled={logPage <= 1}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      ← Anterior
                    </button>
                    <button
                      onClick={() => setLogPage(logPage + 1)}
                      disabled={logPage >= Math.ceil(logTotal / 30)}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      Următor →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Top Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Top 10 Utilizatori după Cost AI</h2>
            </div>

            {topUsers.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                Nu există date încă.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tokens</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Apeluri</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Mediu/Apel</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {topUsers.map((u: any, index: number) => (
                      <tr key={u.userId || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-bold text-gray-900">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {u.userEmail || u.userId || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-green-700">
                          {formatCost(u.totalCost || u._sum?.costUSD || 0)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatTokens(u.totalTokens || u._sum?.totalTokens || 0)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {u.callCount || u._count || 0}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {(u.callCount || u._count || 0) > 0 ? formatCost((u.totalCost || u._sum?.costUSD || 0) / (u.callCount || u._count || 1)) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
